import { Injectable, signal } from "@angular/core";
import { isObservable } from "rxjs";
import { ConfirmDialogOptions } from "../models/confirm-dialog/confirm-dialog-options.model";

@Injectable({ providedIn: "root" })
export class ConfirmDialogService {
  private readonly openState = signal(false);
  private readonly loadingState = signal(false);
  private readonly optionsState = signal<ConfirmDialogOptions | null>(null);

  readonly isOpen = this.openState.asReadonly();
  readonly loading = this.loadingState.asReadonly();
  readonly options = this.optionsState.asReadonly();

  open(options: ConfirmDialogOptions): void {
    this.loadingState.set(false);
    this.optionsState.set({
      confirmText: "Confirm",
      cancelText: "Cancel",
      ...options
    });
    this.openState.set(true);
  }

  confirm(): void {
    const options = this.optionsState();
    if (!options || this.loadingState()) {
      return;
    }

    const result = options.confirmCallback();

    if (isObservable(result)) {
      this.loadingState.set(true);
      result.subscribe({
        next: (success) => {
          this.loadingState.set(false);

          if (success) {
            this.close();
          }
        },
        error: () => {
          this.loadingState.set(false);
        }
      });
      return;
    }

    if (result instanceof Promise) {
      this.loadingState.set(true);
      result
        .then((success) => {
          this.loadingState.set(false);

          if (success) {
            this.close();
          }
        })
        .catch(() => {
          this.loadingState.set(false);
        });
      return;
    }

    this.close();
  }

  cancel(): void {
    if (this.loadingState()) {
      return;
    }

    const options = this.optionsState();
    options?.cancelCallback?.();
    this.close();
  }

  close(): void {
    this.openState.set(false);
    this.loadingState.set(false);
    this.optionsState.set(null);
  }
}
